import { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { isTestSite } from '../../utils/testSite';
import { STATES, getStateByCode } from '../../utils/states';
import { useStateContext, useStateNavigator } from '../../hooks/useStateContext';

// Dropdown options built from our state registry, sorted alphabetically.
// Live states appear first; pre-launch states are marked "Coming soon".
const STATE_OPTIONS = Object.values(STATES).sort((a, b) => {
  if (a.live !== b.live) return a.live ? -1 : 1;
  return a.name.localeCompare(b.name);
});

// Backwards-compatible export used elsewhere in the app.
export const useSelectedState = () => {
  const { state } = useStateContext();
  return state.code;
};

export default function Header() {
  const { user, logout, isAdmin } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const { state: activeState } = useStateContext();
  const switchState = useStateNavigator();
  const location = useLocation();
  // On the neutral directory homepage, show a "Choose your state" prompt instead of
  // defaulting the dropdown to a specific state.
  const onHome = location.pathname === '/';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);
  // Always init 'AZ' so first render matches prerendered HTML; hydrate from
  // the URL/localStorage after mount to avoid hydration mismatches.
  const [selectedState, setSelectedState] = useState('AZ');

  const activeStateCode = activeState?.code;
  useEffect(() => {
    if (activeStateCode) {
      setSelectedState(prev => prev === activeStateCode ? prev : activeStateCode);
    }
  }, [activeStateCode]);

  const handleStateChange = (e) => {
    const code = e.target.value;
    const newState = getStateByCode(code);
    if (!newState) return;
    setSelectedState(code);
    switchState(newState);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const stateName = activeState?.name || 'Arizona';

  return (
    <header style={styles.header}>
      <div className="container" style={styles.inner}>
        {/* Logo */}
        <Link to="/" style={styles.logo} onClick={() => setMobileOpen(false)}>
          <div style={styles.logoMark}>SE</div>
          <div>
            <div style={styles.logoName}>Steady Education</div>
            <div style={styles.logoSub}>Real Estate CE</div>
          </div>
        </Link>

        {/* Desktop Nav — hidden on mobile. Links scope to the active state. */}
        <nav style={styles.nav} className="desktop-only">
          <NavLink to={`/${activeState.slug}/courses`} style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}>
            Courses
          </NavLink>
          <NavLink to={`/${activeState.slug}/how-to-renew`} style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}>
            How to Renew
          </NavLink>
          <NavLink to={`/${activeState.slug}/resources`} style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}>
            Resources
          </NavLink>
          <NavLink to="/about" style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}>
            About
          </NavLink>
          <NavLink to="/contact" style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}>
            Contact
          </NavLink>
          {/* Visible to everyone: /my-courses is a ProtectedRoute, so logged-out clicks
              bounce to /login, which then defaults back to My Courses after sign-in. */}
          <NavLink to="/my-courses" style={({ isActive }) => ({ ...styles.navLink, ...(isActive ? styles.navLinkActive : {}) })}>
            My Courses
          </NavLink>
        </nav>

        {/* State selector — custom dropdown (hidden on mobile, shown in mobile menu) */}
        <div style={styles.stateWrap} className="desktop-only">
          <button style={styles.stateBtn} onClick={() => setStateMenuOpen(o => !o)} aria-haspopup="listbox" aria-expanded={stateMenuOpen}>
            <span style={styles.stateFlag}>◈</span>
            <span style={styles.stateBtnLabel}>{onHome ? 'Choose your state' : (activeState?.name || 'Choose your state')}</span>
            <span style={{ ...styles.stateChevron, transform: stateMenuOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
          </button>
          {stateMenuOpen && (
            <>
              <div style={styles.dropBackdrop} onClick={() => setStateMenuOpen(false)} />
              <div style={styles.stateMenu} role="listbox">
                {STATE_OPTIONS.map(s => (
                  <button
                    key={s.code} style={styles.stateMenuItem} role="option"
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--tan-50)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    onClick={() => { setStateMenuOpen(false); switchState(s); }}
                  >
                    <span style={styles.stateMenuName}>{s.name}</span>
                    {!s.live && <span style={styles.stateMenuSoon}>Coming soon</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right side */}
        <div style={styles.rightSide}>
          {/* Guests get the cart icon once they've added something (guest cart). */}
          {!user && items.length > 0 && (
            <Link to="/cart" style={styles.cartBtn} aria-label="Cart">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              <span style={styles.cartBadge}>{items.length}</span>
            </Link>
          )}
          {user ? (
            <>
              <Link to="/cart" style={styles.cartBtn}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 01-8 0"/>
                </svg>
                {items.length > 0 && <span style={styles.cartBadge}>{items.length}</span>}
              </Link>

              <div style={styles.userMenuWrap} className="desktop-only">
                <button style={styles.userBtn} onClick={() => setUserMenuOpen(!userMenuOpen)}>
                  <div style={styles.avatar}>{user.firstName?.[0]}{user.lastName?.[0]}</div>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{user.firstName}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {userMenuOpen && (
                  <>
                    <div style={styles.dropBackdrop} onClick={() => setUserMenuOpen(false)} />
                    <div style={styles.dropdown}>
                    <Link to="/my-courses" style={styles.dropItem} onClick={() => setUserMenuOpen(false)}>📚 My Courses</Link>
                    <Link to="/my-profile" style={styles.dropItem} onClick={() => setUserMenuOpen(false)}>👤 Profile</Link>
                    {isAdmin && <Link to="/admin" style={styles.dropItem} onClick={() => setUserMenuOpen(false)}>⚙️ Admin Panel</Link>}
                    <div style={styles.dropDivider} />
                    <button style={{ ...styles.dropItem, width: '100%', textAlign: 'left', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }} onClick={handleLogout}>🚪 Sign Out</button>
                  </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 10 }} className="desktop-only">
              <Link to="/login" className="btn btn-ghost btn-sm">Log In</Link>
              <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
            </div>
          )}

          {/* Hamburger — always rendered, hidden on desktop via CSS */}
          <button style={styles.hamburger} className="hamburger-btn" onClick={() => setMobileOpen(!mobileOpen)}>
            <div style={{ ...styles.bar, ...(mobileOpen ? { transform: 'rotate(45deg) translateY(7px)' } : {}) }} />
            <div style={{ ...styles.bar, ...(mobileOpen ? { opacity: 0 } : {}) }} />
            <div style={{ ...styles.bar, ...(mobileOpen ? { transform: 'rotate(-45deg) translateY(-7px)' } : {}) }} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileStateWrap}>
            <span style={{ fontSize: 13, color: 'var(--slate-500)' }}>📍 Your State:</span>
            <select style={{ ...styles.stateSelect, flex: 1 }} value={onHome ? '' : selectedState} onChange={handleStateChange}>
              <option value="" disabled hidden>Choose your state</option>
              {STATE_OPTIONS.map(s => (
                <option key={s.code} value={s.code}>
                  {s.name}{!s.live ? ' — coming soon' : ''}
                </option>
              ))}
            </select>
          </div>
          <Link to={`/${activeState.slug}/courses`} style={styles.mobileLink} onClick={() => setMobileOpen(false)}>Courses</Link>
          <Link to={`/${activeState.slug}/how-to-renew`} style={styles.mobileLink} onClick={() => setMobileOpen(false)}>How to Renew</Link>
          <Link to={`/${activeState.slug}/resources`} style={styles.mobileLink} onClick={() => setMobileOpen(false)}>Resources</Link>
          <Link to="/about" style={styles.mobileLink} onClick={() => setMobileOpen(false)}>About</Link>
          <Link to="/contact" style={styles.mobileLink} onClick={() => setMobileOpen(false)}>Contact</Link>
          <Link to="/my-courses" style={styles.mobileLink} onClick={() => setMobileOpen(false)}>My Courses</Link>
          {user ? (
            <>
              <Link to="/cart" style={styles.mobileLink} onClick={() => setMobileOpen(false)}>Cart ({items.length})</Link>
              <button style={{ ...styles.mobileLink, background: 'none', border: 'none', textAlign: 'left', color: '#dc2626', cursor: 'pointer' }} onClick={handleLogout}>Sign Out</button>
            </>
          ) : (
            <>
              {items.length > 0 && <Link to="/cart" style={styles.mobileLink} onClick={() => setMobileOpen(false)}>Cart ({items.length})</Link>}
              <Link to="/login" style={styles.mobileLink} onClick={() => setMobileOpen(false)}>Log In</Link>
              <Link to="/register" style={{ ...styles.mobileLink, color: 'var(--color-primary)', fontWeight: 700 }} onClick={() => setMobileOpen(false)}>Sign Up</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

const styles = {
  header: { position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' },
  inner: { height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'nowrap' },
  logo: { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit', flexShrink: 0 },
  logoMark: { width: 40, height: 40, background: 'linear-gradient(135deg, #1d4142, #2a5c5e)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 15, letterSpacing: '0.05em' },
  logoName: { fontSize: 16, fontWeight: 700, color: 'var(--slate-900)', lineHeight: 1.2 },
  logoSub: { fontSize: 11, color: 'var(--color-text-muted)', lineHeight: 1 },
  nav: { display: 'flex', alignItems: 'center', gap: 4 },
  navLink: { padding: '6px 14px', borderRadius: 6, fontSize: 14, fontWeight: 500, color: 'var(--slate-600)', textDecoration: 'none', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  navLinkActive: { color: 'var(--color-primary)', background: 'var(--forest-50)' },
  stateWrap: { position: 'relative', flexShrink: 0 },
  stateBtn: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid var(--color-border)', borderRadius: 10, padding: '8px 13px', cursor: 'pointer', font: 'inherit' },
  stateBtnLabel: { fontSize: 14, fontWeight: 600, color: 'var(--forest-600)', whiteSpace: 'nowrap' },
  stateChevron: { fontSize: 11, color: 'var(--slate-400)', lineHeight: 1, transition: 'transform 0.15s' },
  stateMenu: { position: 'absolute', top: '100%', left: 0, marginTop: 8, minWidth: 230, background: 'white', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: 'var(--shadow-lg)', overflow: 'hidden', zIndex: 9999, padding: 6 },
  stateMenuItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', padding: '10px 14px', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left' },
  stateMenuName: { fontSize: 14, fontWeight: 600, color: 'var(--forest-700)' },
  stateMenuSoon: { fontSize: 10.5, fontWeight: 700, color: '#b39e89', background: 'var(--tan-50)', border: '1px solid var(--tan-200)', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' },
  stateFlag: { fontSize: 13, color: '#b39e89', lineHeight: 1 },
  stateSelect: {
    border: 'none', background: 'transparent', fontSize: 14, fontWeight: 600, color: 'var(--forest-600)',
    cursor: 'pointer', outline: 'none', maxWidth: 170,
    appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
    paddingRight: 20,
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 2px center',
  },
  rightSide: { display: 'flex', alignItems: 'center', gap: 12 },
  cartBtn: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 8, color: 'var(--slate-600)', background: 'var(--slate-50)', border: '1px solid var(--slate-200)', textDecoration: 'none', transition: 'all 0.15s' },
  cartBadge: { position: 'absolute', top: -5, right: -5, background: 'var(--color-primary)', color: 'white', fontSize: 10, fontWeight: 700, width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  userMenuWrap: { position: 'relative' },
  userBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'white', border: '1.5px solid var(--slate-200)', borderRadius: 8, cursor: 'pointer', color: 'var(--slate-700)', transition: 'all 0.15s' },
  avatar: { width: 28, height: 28, background: 'linear-gradient(135deg, #1d4142, #2a5c5e)', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 },
  dropBackdrop: { position: 'fixed', inset: 0, zIndex: 9998, background: 'transparent' },
  dropdown: { position: 'absolute', top: '100%', right: 0, marginTop: 6, minWidth: 190, background: 'white', border: '1px solid var(--color-border)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', overflow: 'hidden', zIndex: 9999 },
  dropItem: { display: 'block', padding: '10px 16px', fontSize: 14, color: 'var(--slate-700)', textDecoration: 'none', transition: 'background 0.1s' },
  dropDivider: { height: 1, background: 'var(--color-border)', margin: '4px 0' },
  hamburger: { flexDirection: 'column', gap: 5, padding: 8, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 },
  bar: { width: 22, height: 2, background: 'var(--slate-700)', borderRadius: 2, transition: 'all 0.2s' },
  mobileMenu: { borderTop: '1px solid var(--color-border)', padding: '8px 16px 16px', display: 'flex', flexDirection: 'column', gap: 2 },
  mobileStateWrap: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--tan-50)', borderRadius: 8, marginBottom: 8 },
  mobileLink: { padding: '10px 12px', fontSize: 15, color: 'var(--slate-700)', textDecoration: 'none', borderRadius: 6, display: 'block' },
};